import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ChatWidget from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getStatusMeta } from "@/lib/visaStatus";

export default function Dashboard() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, l] = await Promise.all([api.get("/trips"), api.get("/lookups")]);
    setTrips(t.data.trips);
    setLookups(l.data.lookups);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const removeTrip = async (id) => {
    try {
      await api.delete(`/trips/${id}`);
      setTrips(trips.filter((t) => t.id !== id));
      toast.success("Trip removed");
    } catch {
      toast.error("Could not remove trip");
    }
  };

  return (
    <div className="min-h-screen bg-grid">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold sm:text-4xl">
              Welcome, {user?.full_name?.split(" ")[0]}
            </h1>
            <p className="mt-1 text-slate-600">
              {user?.is_premium ? (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <Sparkles className="h-4 w-4" /> Premium member
                </span>
              ) : (
                "Free plan · Upgrade to unlock premium reports"
              )}
            </p>
          </div>
          <Button asChild data-testid="new-lookup-btn" className="bg-slate-900 hover:bg-slate-800">
            <Link to="/checker">
              <Plus className="mr-2 h-4 w-4" /> New visa check
            </Link>
          </Button>
        </div>

        <section className="mt-8">
          <h2 className="font-heading text-lg font-semibold">Saved trips</h2>
          {loading ? (
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-40 w-full rounded-2xl" />
              ))}
            </div>
          ) : trips.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center">
              <Compass className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">No saved trips yet.</p>
              <Button asChild variant="link" className="mt-1">
                <Link to="/checker">Plan your first trip →</Link>
              </Button>
            </div>
          ) : (
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {trips.map((t) => {
                const meta = getStatusMeta(t.lookup?.result?.visa_status);
                return (
                  <div
                    key={t.id}
                    data-testid={`trip-card-${t.id}`}
                    className="group relative rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-teal-500/60 hover:shadow-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-xs font-mono text-slate-400">
                          {t.lookup?.nationality} → {t.lookup?.destination}
                        </div>
                        <h3 className="mt-1 font-heading font-semibold">
                          {t.lookup?.destination_name}
                        </h3>
                        <div className="mt-0.5 text-xs text-slate-500 capitalize">
                          {t.lookup?.purpose} · {t.lookup?.duration_days} days
                        </div>
                      </div>
                      <button
                        onClick={() => removeTrip(t.id)}
                        className="rounded-lg p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                        data-testid={`delete-trip-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div
                      className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.className}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                      {t.lookup?.result?.visa_status_label || meta.label}
                    </div>
                    <div className="mt-4 border-t border-slate-100 pt-3">
                      <Link
                        to={`/results/${t.lookup_id}`}
                        className="text-sm font-medium text-teal-600 hover:text-teal-700"
                      >
                        View details →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-12">
          <h2 className="font-heading text-lg font-semibold">Recent lookups</h2>
          {lookups.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No recent lookups.</p>
          ) : (
            <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {lookups.map((l) => {
                const meta = getStatusMeta(l.result?.visa_status);
                return (
                  <Link
                    key={l.id}
                    to={`/results/${l.id}`}
                    data-testid={`lookup-row-${l.id}`}
                    className="flex items-center justify-between px-5 py-4 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {l.nationality_name} → {l.destination_name}
                      </div>
                      <div className="text-xs text-slate-500 capitalize">
                        {l.purpose} · {l.duration_days} days
                      </div>
                    </div>
                    <span
                      className={`ml-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.className}`}
                    >
                      {l.result?.visa_status_label || meta.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <ChatWidget />
    </div>
  );
}
